import React, { useContext, useEffect, useMemo, useState } from "react";

import { useTranslation } from 'react-i18next';
import { 
  Box, Typography, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Snackbar, IconButton, Alert, CircularProgress,
  Grid, Card, CardContent, CardActions, MenuItem, InputLabel, FormControl, Select,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import { fetchJson } from "../../../utils/fetchJson";
import { UserContext } from "../../../contexts/UserContext";

export default function InvoicesPage() {
  const { t } = useTranslation();
  const user = useContext(UserContext);

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState(null);
  const [form, setForm] = useState({ 
    schoolId: "", 
    planId: "", 
    periodStart: "", 
    periodEnd: "",
    dueDate: ""
  });
  const [schools, setSchools] = useState([]);
  const [plans, setPlans] = useState([]);
  const [snack, setSnack] = useState("");

  const canManage = useMemo(() => {
    if (user?.role === 'SystemAdmin') return true;
    const perms = user && Array.isArray(user.systemPermissions) ? user.systemPermissions : null;
    if (!perms) return true;
    if (perms.includes('*')) return true;
    return perms.includes('billing');
  }, [user]);

  useEffect(() => { 
    loadInvoices();
    loadSchools();
    loadPlans();
  }, []);

  async function loadInvoices() {
    setLoading(true); setError("");
    try {
      const data = await fetchJson("/invoices");
      setInvoices(data);
    } catch (e) { setError(e.message) }
    finally { setLoading(false) }
  }

  async function loadSchools() {
    try {
      const data = await fetchJson("/schools");
      setSchools(data.schools || []); // FIX: use the array property
    } catch (e) { console.error("Failed to load schools", e) }
  }

  async function loadPlans() {
    try {
      const data = await fetchJson("/subscription-plans");
      setPlans(data);
    } catch (e) { console.error("Failed to load plans", e) }
  }

  function openEdit(invoice) {
    if (!canManage) return;
    setCurrentInvoice(invoice);
    setDialogOpen(true);
  }

  function openGenerate() {
    if (!canManage) return;
    setGenerateDialogOpen(true);
  }

  async function handleGenerate() {
    if (!canManage) return;
    setError("");
    try {
      const newInvoice = await fetchJson("/invoices/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });
      
      setSnack(t('invoices.generated', 'Invoice generated'));
      setGenerateDialogOpen(false);
      loadInvoices();
    } catch (e) { setError(e.message) }
  }

  async function downloadInvoice(id) {
    try {
      const invoice = invoices.find(i => i.id === id);
      if (invoice && invoice.pdfPath) {
        // In a real app, this would download the file
        window.open(invoice.pdfPath, '_blank');
      }
    } catch (e) {
      setError(t('invoices.downloadFailed', 'Failed to download invoice'));
    }
  }

  const columns = [
    { field: "invoiceNumber", headerName: t('invoices.invoiceNumber', 'Invoice #'), flex: 1 },
    { field: "school.name", headerName: t('invoices.school', 'School'), flex: 1, valueGetter: params => params.row.School?.name },
    { field: "total", headerName: t('invoices.amount', 'Amount'), width: 120, valueGetter: params => `$${params.value.toFixed(2)}` },
    { field: "dueDate", headerName: t('invoices.dueDate', 'Due Date'), width: 120, valueGetter: params => new Date(params.value).toLocaleDateString() },
    { field: "status", headerName: t('invoices.status', 'Status'), width: 120 },
    {
      field: "actions",
      headerName: t('invoices.actions', 'Actions'),
      type: "actions",
      width: 150,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<DownloadIcon />}
          label={t('invoices.download', 'Download')}
          onClick={() => downloadInvoice(params.id)}
        />,
        <GridActionsCellItem
          icon={<EditIcon />}
          label={t('invoices.edit', 'Edit')}
          onClick={() => openEdit(params.row)}
          disabled={!canManage}
        />,
        <GridActionsCellItem
          icon={<PrintIcon />}
          label={t('invoices.print', 'Print')}
          onClick={() => window.print()}
        />
      ]
    }
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 4 } }}>
      <Typography variant="h4" gutterBottom>{t('invoices.title', 'Invoice Management')}</Typography>
      <Button variant="contained" startIcon={<AddIcon />} onClick={openGenerate} sx={{ mb: 2 }} disabled={!canManage}>
        {t('invoices.generate', 'Generate Invoice')}
      </Button>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ height: 600, width: "100%" }}>
          <DataGrid 
            rows={invoices} 
            columns={columns} 
            pageSize={10}
            disableSelectionOnClick
          />
        </Box>
      )}

      {/* Generate Invoice Dialog */}
      <Dialog open={generateDialogOpen} onClose={() => setGenerateDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('invoices.generateTitle', 'Generate New Invoice')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }} width={"100%"}>
            <Grid item xs={12}>
              <FormControl fullWidth size="large" sx={{ minWidth: 300 }}>
                <InputLabel>{t('invoices.school', 'School')}</InputLabel>
                <Select
                  value={form.schoolId}
                  onChange={e => setForm(f => ({ ...f, schoolId: e.target.value }))}
                  label={t('invoices.school', 'School')}
                >
                  {schools.map(school => (
                    <MenuItem key={school.id} value={school.id}>{school.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="large" sx={{ minWidth: 300 }}>
                <InputLabel>{t('invoices.subscriptionPlan', 'Subscription Plan')}</InputLabel>
                <Select
                  value={form.planId}
                  onChange={e => setForm(f => ({ ...f, planId: e.target.value }))}
                  label={t('invoices.subscriptionPlan', 'Subscription Plan')}
                >
                  {plans.map(plan => (
                    <MenuItem key={plan.id} value={plan.id}>{plan.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('invoices.periodStart', 'Period Start')}
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.periodStart}
                onChange={e => setForm(f => ({ ...f, periodStart: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('invoices.periodEnd', 'Period End')}
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.periodEnd}
                onChange={e => setForm(f => ({ ...f, periodEnd: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t('invoices.dueDate', 'Due Date')}
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.dueDate}
                onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateDialogOpen(false)}>{t('invoices.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={handleGenerate} disabled={!canManage}>{t('invoices.generate', 'Generate')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={3000}
        onClose={() => setSnack("")}
        message={snack}
        action={
          <IconButton size="small" color="inherit" onClick={() => setSnack("")}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </Box>
  );
}